/**
 * LA BITÁCORA INTERNA DE UN TORNEO, Y QUIÉNES LO ORGANIZAN.
 *
 * QUÉ PROBLEMA RESUELVE. Lo que quien organiza necesita acordarse —que la
 * cancha 3 está en refacción, que fulano avisó que llega tarde, que hay que
 * devolverle la inscripción a alguien— vive hoy en un chat de WhatsApp donde
 * se pierde entre mensajes. Acá queda pegado al torneo, en orden, y lo lee
 * quien entre a organizarlo aunque no estuviera en esa conversación.
 *
 * NO LA VE QUIEN JUEGA. Es el punto entero: una nota que la gente del torneo
 * puede leer es una nota que nadie escribe con franqueza. El componente entero
 * cuelga de `canManage`, y el servidor contesta 403 igual — la pantalla no es
 * la que guarda el secreto, solo evita mostrar una puerta que no abre.
 *
 * LAS NOTAS NO SE EDITAN NI SE BORRAN, y por eso no hay botones para eso: es
 * una bitácora, y lo que la hace útil es que lo escrito quede.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TOURNAMENT_NOTE_LIMITS,
  type CommunityMember,
  type AdminInvitation,
  type TournamentAdmin,
  type TournamentNote,
} from '@tennisup/contracts';

import { Alert } from './Alert';
import { Avatar } from './Avatar';
import { Select } from './Select';
import { api, assetUrl } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';

export function TournamentNotes({
  tournamentId,
  communityId,
  /** Si quien mira administra el CLUB: solo ese puede tocar el equipo. */
  puedeNombrar,
}: {
  tournamentId: string;
  communityId: string;
  puedeNombrar: boolean;
}): JSX.Element | null {
  const { t, i18n } = useTranslation();

  const [notas, setNotas] = useState<TournamentNote[] | null>(null);
  const [equipo, setEquipo] = useState<TournamentAdmin[]>([]);
  const [texto, setTexto] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const [n, e] = await Promise.all([
        api.listTournamentNotes(tournamentId),
        api.listTournamentAdmins(tournamentId),
      ]);
      /*
        `Array.isArray` Y NO CONFIAR EN EL TIPO. TypeScript promete un arreglo,
        pero lo que llega es JSON de la red: un proxy que devuelve un HTML de
        error, una respuesta a medias o un servidor que contesta otra cosa
        entran igual, y `notas.map` revienta la pantalla ENTERA del torneo —no
        solo esta sección—. Es el mismo resguardo que usa `ClubReports`.
      */
      setNotas(Array.isArray(n) ? n : []);
      setEquipo(Array.isArray(e) ? e : []);
    } catch {
      // NO SE DICE NADA SI FALLA. Quien no puede organizar recibe un 403 acá,
      // y eso no es un error que mostrarle: es la respuesta correcta.
      setNotas(null);
    }
  }, [tournamentId]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function escribir(): Promise<void> {
    const limpio = texto.trim();
    if (limpio === '') return;
    setEnviando(true);
    setError(null);
    try {
      const nueva = await api.addTournamentNote(tournamentId, limpio);
      // SE AGREGA AL FINAL, que es donde va la última de una bitácora.
      setNotas((antes) => [...(antes ?? []), nueva]);
      setTexto('');
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setEnviando(false);
    }
  }

  // `null` mientras no se sabe, y también cuando el servidor dijo que no.
  if (notas === null) return null;

  return (
    <section className="tournament-notes">
      <h2>{t('notes.title')}</h2>
      <p className="help">{t('notes.help')}</p>

      {puedeNombrar && (
        <Organizadores
          tournamentId={tournamentId}
          communityId={communityId}
          equipo={equipo}
          onChanged={setEquipo}
        />
      )}

      {error !== null && <Alert kind="error">{error}</Alert>}

      {notas.length === 0 ? (
        <p className="help">{t('notes.empty')}</p>
      ) : (
        <ul className="note-list">
          {notas.map((nota) => (
            <li key={nota.id} className="note-card">
              <div className="note-head">
                <Avatar
                  url={assetUrl(nota.authorAvatarPath)}
                  name={nota.authorName}
                  size={28}
                />
                <strong>{nota.authorName}</strong>
                <span className="help">{cuando(nota.createdAt, i18n.language)}</span>
              </div>
              {/* `pre-wrap` en el CSS: los saltos de línea que alguien
                  escribió son parte de lo que quiso decir. */}
              <p className="note-body">{nota.body}</p>
            </li>
          ))}
        </ul>
      )}

      {/* EL FORMULARIO AL FINAL, debajo de la última nota: es donde está el
          ojo después de leerlas, y es el orden en que se escribe una más. */}
      <div className="note-new">
        <label className="field-label" htmlFor="nota-nueva">
          {t('notes.add')}
        </label>
        <textarea
          id="nota-nueva"
          rows={3}
          value={texto}
          maxLength={TOURNAMENT_NOTE_LIMITS.bodyMax}
          disabled={enviando}
          placeholder={t('notes.placeholder')}
          onChange={(e) => setTexto(e.target.value)}
        />
        <p className="button-row">
          <button type="button" disabled={enviando || texto.trim() === ''} onClick={() => void escribir()}>
            {t('notes.send')}
          </button>
        </p>
      </div>
    </section>
  );
}

/**
 * QUIÉNES AYUDAN A ORGANIZAR ESTE TORNEO.
 *
 * SOLO LO VE Y LO TOCA QUIEN ADMINISTRA EL CLUB. Un organizador de torneo no
 * puede nombrar a otro: si pudiera, el permiso se propagaría solo y el club
 * dejaría de controlar a quién le abrió la puerta. Dar permiso es una decisión
 * del club; usarlo, del torneo.
 */
function Organizadores({
  tournamentId,
  communityId,
  equipo,
  onChanged,
}: {
  tournamentId: string;
  communityId: string;
  equipo: TournamentAdmin[];
  onChanged: (equipo: TournamentAdmin[]) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [miembros, setMiembros] = useState<CommunityMember[]>([]);
  const [elegido, setElegido] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendientes, setPendientes] = useState<AdminInvitation[]>([]);
  const [recienInvitado, setRecienInvitado] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const filas = await api.listMembers(communityId);
        setMiembros(Array.isArray(filas) ? filas : []);
      } catch {
        setMiembros([]);
      }
    })();
  }, [communityId]);

  /*
   * LAS INVITACIONES QUE ESPERAN RESPUESTA.
   *
   * Sin verlas, invitar es un correo que se fue sin dejar rastro: el
   * desplegable seguiría ofreciendo a la misma persona y quien organiza
   * volvería a apretar el botón porque en la pantalla no pasó nada.
   */
  useEffect(() => {
    void (async () => {
      try {
        const filas = await api.listTournamentAdminInvitations(tournamentId);
        setPendientes(Array.isArray(filas) ? filas : []);
      } catch {
        setPendientes([]);
      }
    })();
  }, [tournamentId, recienInvitado]);

  async function hacer(accion: () => Promise<TournamentAdmin[]>): Promise<void> {
    setOcupado(true);
    setError(null);
    try {
      const equipo = await accion();
      onChanged(Array.isArray(equipo) ? equipo : []);
      setElegido('');
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setOcupado(false);
    }
  }

  /**
   * INVITA a organizar. No suma a nadie: manda el correo con el enlace.
   *
   * Por eso no pasa por `hacer`, que espera el equipo de vuelta: acá el
   * equipo NO cambió todavía. Lo que cambia es la lista de pendientes.
   */
  async function invitar(): Promise<void> {
    setOcupado(true);
    setError(null);
    try {
      await api.addTournamentAdmin(tournamentId, elegido);
      setElegido('');
      setRecienInvitado((antes) => !antes);
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setOcupado(false);
    }
  }

  /** Cancela una que se mandó por error: su enlace deja de servir. */
  async function cancelar(invitacion: AdminInvitation): Promise<void> {
    setOcupado(true);
    setError(null);
    try {
      await api.revokeTournamentAdminInvitation(tournamentId, invitacion.id);
      setRecienInvitado((antes) => !antes);
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setOcupado(false);
    }
  }

  // Los que ya están no se ofrecen otra vez: agregarlos no falla, pero un
  // desplegable que ofrece lo que ya hiciste es un desplegable que confunde.
  // Los ya invitados tampoco: ahí el servidor SÍ falla, y el botón sería una
  // trampa.
  const yaEstan = new Set(equipo.map((a) => a.userId));
  for (const invitacion of pendientes) yaEstan.add(invitacion.userId);
  const disponibles = miembros.filter((m) => !yaEstan.has(m.userId));

  return (
    <div className="note-team">
      <h3>{t('notes.team')}</h3>
      <p className="help">{t('notes.teamHelp')}</p>

      {error !== null && <Alert kind="error">{error}</Alert>}

      {equipo.length > 0 && (
        <ul className="team-list">
          {equipo.map((admin) => (
            <li key={admin.userId}>
              <Avatar url={assetUrl(admin.avatarPath)} name={admin.displayName} size={28} />
              <span>{admin.displayName}</span>
              <span className="help">{t('notes.addedBy', { name: admin.addedByName })}</span>
              <button
                type="button"
                className="link"
                disabled={ocupado}
                onClick={() =>
                  void hacer(() => api.removeTournamentAdmin(tournamentId, admin.userId))
                }
              >
                {t('notes.remove')}
              </button>
            </li>
          ))}
        </ul>
      )}

      {pendientes.length > 0 && (
        <ul className="team-list">
          {pendientes.map((invitacion) => (
            <li key={invitacion.id}>
              <Avatar
                url={assetUrl(invitacion.avatarPath)}
                name={invitacion.displayName}
                size={28}
              />
              <span>{invitacion.displayName}</span>
              <span className="help">{t('adminInvite.pending')}</span>
              <button
                type="button"
                className="link"
                disabled={ocupado}
                onClick={() => void cancelar(invitacion)}
              >
                {t('adminInvite.revoke')}
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="team-add">
        {/* SIN `aria-label` ESTE CAMPO NO TIENE NOMBRE. No hay una `<label>`
            al lado —el texto que lo explica es el del título de la sección—
            así que un lector de pantalla lo anunciaba como «botón» a secas, y
            quien no ve la pantalla no tenía forma de saber qué elige acá. */}
        <Select
          id="nuevo-organizador"
          aria-label={t('notes.choose')}
          value={elegido}
          options={[
            { value: '', label: t('notes.choose') },
            ...disponibles.map((m) => ({ value: m.userId, label: m.displayName })),
          ]}
          onChange={setElegido}
        />
        <button
          type="button"
          disabled={ocupado || elegido === ''}
          onClick={() => void invitar()}
        >
          {t('notes.add2')}
        </button>
      </div>
    </div>
  );
}

/**
 * CUÁNDO SE ESCRIBIÓ, con la hora: dos notas del mismo día se distinguen por
 * eso, y en un torneo que dura un fin de semana el día solo no alcanza.
 */
function cuando(iso: string, locale: string): string {
  const fecha = new Date(iso);
  if (Number.isNaN(fecha.getTime())) return iso;
  try {
    return fecha.toLocaleString(locale, {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
